/**
 * Measurements for the SEO fields.
 *
 * Ported from the Blog Handover Tool's `computeSeoChecks`, with one addition: Google
 * truncates a title by **pixel width**, not character count, so "WILLIAM WILLIAMSON" and
 * "iiii iiii iiii" behave completely differently at the same 55 characters. We show both —
 * the character count because that is what the SEO guide specifies, and the pixel width
 * because that is what actually gets cut off.
 */

/** Truncation starts around here; treat anything beyond as at risk. */
export const TITLE_PX_LIMIT = 530;
export const TITLE_CHAR_LIMIT = 55;
export const DESC_CHAR_LIMIT = 155;

/** Collctiv's own domains. Anything else is an external link. */
export const INTERNAL_HOSTS = /(^|\.)collctiv\.com$|(^|\.)collctiv\.app\.link$/i;

let ctx: CanvasRenderingContext2D | null | undefined;

/**
 * Approximate the rendered width of a search result title.
 *
 * Google renders desktop titles in roughly 20px Arial. Measuring in a canvas is the closest
 * we can get without screen-scraping a SERP.
 */
export function measureTitlePx(text: string): number {
  if (ctx === undefined) {
    try {
      ctx = document.createElement('canvas').getContext('2d');
      if (ctx) ctx.font = '20px Arial, sans-serif';
    } catch {
      ctx = null;
    }
  }
  if (!ctx) {
    // No canvas (tests, SSR). Fall back to an average advance rather than pretending to know.
    return Math.round(text.length * 9.6);
  }
  return Math.round(ctx.measureText(text).width);
}

/** Lowercase, hyphenated, no punctuation. Matches the Blog Handover Tool's behaviour. */
export function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

export interface FieldCheck {
  status: 'ok' | 'warn' | 'error' | 'info';
  message: string;
}

const has = (haystack: string, needle: string): boolean =>
  !!needle && haystack.toLowerCase().includes(needle.toLowerCase());

export function checkSlug(slug: string, keyword: string): FieldCheck | null {
  if (!slug) return { status: 'warn', message: 'No slug set.' };
  if (!/^[a-z0-9-]+$/.test(slug)) {
    return { status: 'error', message: 'Lowercase letters, numbers and hyphens only.' };
  }
  // The SEO guide is explicit: no dates, no filler.
  if (/\b(19|20)\d{2}\b/.test(slug)) {
    return { status: 'warn', message: 'Contains a year — drop dates from slugs.' };
  }
  if (keyword && !slug.includes(slugify(keyword))) {
    return { status: 'warn', message: 'Doesn’t contain the primary keyword.' };
  }
  return { status: 'ok', message: 'Clean, and includes the keyword.' };
}

export function checkMetaTitle(title: string, keyword: string): FieldCheck | null {
  if (!title) return { status: 'warn', message: 'Not set.' };
  if (title.length > TITLE_CHAR_LIMIT) {
    return { status: 'warn', message: `${title.length} characters — aim for ${TITLE_CHAR_LIMIT} or fewer.` };
  }
  if (measureTitlePx(title) > TITLE_PX_LIMIT) {
    return { status: 'warn', message: 'Wide enough that Google may truncate it.' };
  }
  if (keyword && !has(title, keyword)) {
    return { status: 'warn', message: 'Doesn’t contain the primary keyword.' };
  }
  if (keyword && title.toLowerCase().indexOf(keyword.toLowerCase()) > title.length / 2) {
    return { status: 'info', message: 'Good — the keyword would be stronger nearer the start.' };
  }
  return { status: 'ok', message: 'Good length, includes the keyword.' };
}

export function checkMetaDescription(desc: string, keyword: string): FieldCheck | null {
  if (!desc) return { status: 'warn', message: 'Not set.' };
  if (desc.length > DESC_CHAR_LIMIT) {
    return { status: 'warn', message: `${desc.length} characters — max ${DESC_CHAR_LIMIT}.` };
  }
  if (keyword && !has(desc, keyword)) {
    return { status: 'warn', message: 'Doesn’t contain the primary keyword.' };
  }
  return { status: 'ok', message: 'Good length, includes the keyword.' };
}

/** `[Keyword] - [Benefit] | Collctiv`, the format the SEO guide specifies. */
export function suggestMetaTitle(pageTitle: string): string {
  const base = pageTitle.trim().replace(/\s*\|\s*Collctiv\s*$/i, '');
  return base ? `${base} | Collctiv` : '';
}
